import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { getStudent, MODE_LABELS } from "@/lib/student";

const BAR = { definizioni: "bg-[var(--sky)]", fumetti: "bg-[var(--coral)]", mappe: "bg-[var(--mint)]" };
import { StudentGate } from "@/components/StudentGate";
import { BadgeGrid } from "@/components/BadgeGrid";

const pct = (r) => Math.round((r.score / r.total) * 100);
const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function Progress() {
  const [student, setStudentState] = useState(getStudent);
  const [results, setResults] = useState(null);
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    if (!student) { setBadges([]); return setResults(null); }
    const params = { student_name: student.name, class_id: student.class_id };
    api.get("/quiz-results", { params }).then((r) => setResults(r.data));
    api.get("/badges", { params }).then((r) => setBadges(r.data));
  }, [student]);

  const recent = (results || []).slice(0, 12).reverse();
  const avg = results?.length ? Math.round(results.reduce((a, r) => a + pct(r), 0) / results.length) : 0;

  return (
    <main className="mx-auto max-w-4xl px-5 pb-24 pt-10 sm:px-8" data-testid="progress-page">
      <h1 className="font-display text-4xl font-bold sm:text-5xl">I miei progressi</h1>
      <div className="mt-6"><StudentGate student={student} onChange={setStudentState} /></div>
      {student && !results && <Loader2 className="mx-auto mt-10 h-8 w-8 animate-spin" />}
      {badges.length > 0 && (
        <section className="mt-8" data-testid="badges-section">
          <h2 className="font-display mb-4 text-base font-bold uppercase tracking-wide md:text-lg">
            I miei badge · <span data-testid="badges-earned-count">{badges.filter((b) => b.earned).length}</span>/{badges.length}
          </h2>
          <BadgeGrid badges={badges} />
        </section>
      )}
      {results && results.length === 0 && (
        <p className="ink-box mt-8 rounded-3xl bg-white p-10 text-center font-bold" data-testid="progress-empty">
          Nessun quiz ancora. <Link to="/quiz" className="underline">Fai il primo quiz!</Link>
        </p>
      )}
      {results?.length > 0 && (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="ink-box-sm rounded-2xl bg-[var(--sun)] p-4"><p className="font-display text-3xl font-bold" data-testid="progress-count">{results.length}</p><p className="text-xs font-bold uppercase">Quiz fatti</p></div>
            <div className="ink-box-sm rounded-2xl bg-[var(--mint)] p-4"><p className="font-display text-3xl font-bold" data-testid="progress-avg">{avg}%</p><p className="text-xs font-bold uppercase">Media risposte giuste</p></div>
          </div>
          <section className="ink-box mt-8 rounded-3xl bg-white p-6" data-testid="progress-chart">
            <h2 className="font-display flex items-center gap-2 text-base font-bold md:text-lg"><TrendingUp className="h-5 w-5" /> Andamento ultimi quiz</h2>
            <div className="mt-6 flex h-44 items-end gap-2">
              {recent.map((r) => (
                <div key={r.id} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                  <span className="text-[11px] font-extrabold">{pct(r)}%</span>
                  <div className={`w-full rounded-t-lg border-2 border-[var(--ink)] ${BAR[r.mode] || "bg-[var(--sky)]"}`} style={{ height: `${Math.max(pct(r), 4)}%` }} />
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs font-bold text-neutral-500"><span className="text-[var(--sky)]">■</span> Quiz definizioni · <span className="text-[var(--coral)]">■</span> Quiz fumetti · <span className="text-[var(--mint)]">■</span> Gioco mappe</p>
          </section>
          <ul className="mt-6 space-y-2" data-testid="progress-list">
            {results.map((r) => (
              <li key={r.id} className="flex items-center justify-between rounded-xl border-2 border-[var(--ink)] bg-white px-4 py-2 text-sm font-semibold">
                <span>{fmt(r.created_at)} · {MODE_LABELS[r.mode] || r.mode}{r.subject ? ` · ${r.subject}` : ""}</span>
                <span className="font-extrabold">{r.score}/{r.total}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </main>
  );
}
